import React from 'react'
import { DebugAdUnit } from './DebugAdUnit'
import { createAdViewableEvent } from '../ad-helpers/viewability-events'
import { usePrevious } from 'common/hooks/usePrevious'
import { AdSlotProps } from './AdUnitWrapper'
import { useWindowWidth } from 'component-library/__helpers/global-dom-events'
import { AdUnitSize } from 'common/advertising/ad-types'
import { AdEvent } from 'common/events/viewability-event'
import { FeatureToggle } from 'component-library/feature-toggling/Toggle/Toggle'
import { SlotRenderEndedEvent } from '../gpt/GptAdProvider/components/GptManager'
import { GptAdSlot } from '../gpt/GptAdSlot/GptAdSlot'

export interface AdUnitProps extends AdSlotProps {
    unitSize?: AdUnitSize
    onSlotRenderEnded: (event: SlotRenderEndedEvent) => void
    onSlotMutation?: (mutations: MutationRecord[]) => void
    breakoutContainerRef: React.MutableRefObject<HTMLDivElement | null>
    isViewable: boolean
    threshold: number
    isLoaded?: boolean | undefined
    onEvent: (event: AdEvent) => void
}

export const AdUnit: React.FC<AdUnitProps> = ({
    unitId,
    adState,
    isViewable,
    isLoaded,
    onSlotRenderEnded,
    breakoutContainerRef,
    onSlotMutation,
    onEvent,
    threshold,
}) => {
    const [hasTriggeredViewable, setHasTriggeredViewable] = React.useState(false)
    const prevIsLoaded = usePrevious(isLoaded)
    const windowWidth = useWindowWidth()
    const unit = adState.unitLookup(unitId, windowWidth)
    React.useEffect(() => {
        if (isLoaded && !prevIsLoaded && unit) {
            onEvent(createAdViewableEvent('adunit.loaded', unit, threshold))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, prevIsLoaded, unit])

    React.useEffect(() => {
        if (isViewable && isLoaded && !hasTriggeredViewable && unit) {
            setHasTriggeredViewable(true)
            onEvent(createAdViewableEvent('adunit.viewable', unit, threshold))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, isViewable, hasTriggeredViewable, unit])

    // Callback should only be used for real GptAdSlot - not the debug version
    const handleSlotRenderEnded = React.useCallback(
        (e: googletag.events.SlotRenderEndedEvent) => {
            if (unit) onSlotRenderEnded({ ...e, unit })
        },
        [unit, onSlotRenderEnded]
    )

    if (!unit) {
        return null
    }

    return (
        <FeatureToggle
            feature="debug-ads"
            on={() => (
                <DebugAdUnit
                    isViewable={isViewable}
                    unit={unit}
                    onSlotRenderEnded={onSlotRenderEnded}
                />
            )}
            off={() => (
                <GptAdSlot
                    adContainerRef={breakoutContainerRef}
                    onSlotRenderEnded={handleSlotRenderEnded}
                    onSlotMutation={onSlotMutation}
                    id={unit.id}
                />
            )}
        />
    )
}
